import React, { useEffect, useMemo, useState } from "react";
import { playGiftRainSfx } from "@/lib/giftSounds";

/**
 * Live Gift Rain — full-screen coin-shower celebration that fires when a
 * high-tier gift (Rocket / Crown / Galaxy) lands during a livestream.
 *
 * The parent (`LiveRoom.jsx`) calls `pushGift({icon, label, coins, sender})`
 * whenever a gift data-packet arrives with `coins >= 500`. We queue it,
 * render one shower at a time (to keep the room readable), and auto-fade
 * out based on the tier's duration.
 *
 * Rendering strategy — pure CSS particles:
 *   * ~30-100 emoji "coins" per tier, each with randomized `left`,
 *     `animation-delay`, and `font-size`. `position:absolute` + a shared
 *     `@keyframes coinRain` keeps the DOM cheap even at 100 particles.
 *   * The gift's own icon is rendered HUGE (12rem) at the center with a
 *     slower pop-in / drift-away animation, so the tier is instantly
 *     recognizable.
 *   * Backdrop is a subtle radial gradient burst — dark enough to fade
 *     the video underneath but transparent enough that viewers still see
 *     the broadcaster reacting.
 */

const TIER_SPEC = {
  rocket: { count: 30, duration: 3500, char: "🪙", accent: "#FF007F", label: "ROCKET" },
  crown:  { count: 60, duration: 5000, char: "👑", accent: "#FFD93D", label: "CROWN" },
  galaxy: { count: 100, duration: 7500, char: "✨", accent: "#39FF14", label: "GALAXY" },
};

// Given the gift coin value, pick the right tier spec. Anything below
// 500 skips the rain entirely (small gifts already have the floating
// pop-up handled by LiveRoom.jsx directly).
function tierFor(coins) {
  if (coins >= 5000) return "galaxy";
  if (coins >= 1000) return "crown";
  if (coins >= 500)  return "rocket";
  return null;
}

export default function LiveGiftRain({ activeGift, onDone }) {
  const [visible, setVisible] = useState(false);

  const tier = activeGift ? tierFor(activeGift.coins) : null;
  const spec = tier ? TIER_SPEC[tier] : null;

  // Freeze the particle randomness per-render so re-renders don't reshuffle
  // mid-animation. Regenerated only when the underlying gift changes.
  const particles = useMemo(() => {
    if (!spec) return [];
    return Array.from({ length: spec.count }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,                 // 0..100 vw
      size: 16 + Math.random() * 32,             // 16..48 px
      delay: Math.random() * (spec.duration * 0.5), // stagger
      duration: 1600 + Math.random() * 1400,     // 1.6-3s fall time
      rotate: Math.random() * 720 - 360,
      char: Math.random() < 0.6 ? "🪙" : spec.char,
    }));
  }, [spec, activeGift]);

  useEffect(() => {
    if (!activeGift || !spec) { setVisible(false); return; }
    setVisible(true);
    // Fire the audio cascade at the same tick the visual mounts so both
    // land in perfect sync. `playGiftRainSfx` is a no-op when the user
    // has muted or the audio context is still suspended, so this can't
    // throw.
    playGiftRainSfx(activeGift.coins);
    const t = setTimeout(() => {
      setVisible(false);
      onDone?.();
    }, spec.duration);
    return () => clearTimeout(t);
  }, [activeGift, spec, onDone]);

  if (!visible || !spec || !activeGift) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-40 overflow-hidden"
      data-testid="live-gift-rain"
      style={{
        background:
          `radial-gradient(circle at center, ${spec.accent}22 0%, transparent 70%)`,
      }}
    >
      {/* Falling coins */}
      {particles.map((p) => (
        <span
          key={p.id}
          className="absolute select-none animate-coin-rain"
          style={{
            left: `${p.left}vw`,
            fontSize: `${p.size}px`,
            animationDelay: `${p.delay}ms`,
            animationDuration: `${p.duration}ms`,
            top: "-8vh",
            filter: `drop-shadow(0 0 6px ${spec.accent})`,
            transform: `rotate(${p.rotate}deg)`,
          }}
        >
          {p.char}
        </span>
      ))}
      {/* Center hero — the tier's own gift icon, mega-size */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="animate-gift-hero flex flex-col items-center gap-2"
          style={{ filter: `drop-shadow(0 0 40px ${spec.accent})` }}
        >
          <span className="text-[10rem] md:text-[14rem] leading-none">
            {activeGift.icon || spec.char}
          </span>
          <div
            className="font-mono text-sm md:text-lg uppercase tracking-[0.3em] px-4 py-1 border"
            style={{ color: spec.accent, borderColor: spec.accent }}
          >
            {spec.label} · {activeGift.coins.toLocaleString()} COINS
          </div>
          {activeGift.sender && (
            <p className="text-white text-xs md:text-base font-bold mt-1">
              from <span style={{ color: spec.accent }}>{activeGift.sender}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
